import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../../services/api';
import {
  ArrowPathIcon,
  BeakerIcon,
  BoltIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  ShieldCheckIcon,
  SignalIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const CATEGORY_COLORS = {
  Engagement: 'bg-violet-50 text-violet-700 ring-violet-600/20',
  Growth: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  Commerce: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  Security: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  Community: 'bg-cyan-50 text-cyan-700 ring-cyan-600/20',
  Moderation: 'bg-rose-50 text-rose-700 ring-rose-600/20',
};

const Metric = ({ label, value, note, icon: Icon, tone = 'slate' }) => {
  const tones = {
    slate: 'bg-slate-900 text-white',
    cyan: 'bg-cyan-500 text-white',
    rose: 'bg-rose-50 text-rose-950 border border-rose-100',
    white: 'bg-white text-slate-950 border border-slate-200',
  };
  return (
    <div className={`${tones[tone]} rounded-2xl p-5 shadow-sm`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium opacity-70">{label}</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight">{value}</p>
          <p className="mt-1 text-xs opacity-60">{note}</p>
        </div>
        <Icon className="h-6 w-6 opacity-70" />
      </div>
    </div>
  );
};

const Toggle = ({ checked, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
    className={`relative inline-flex h-7 w-12 flex-none items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-50 ${checked ? 'bg-cyan-500' : 'bg-slate-300'}`}
  >
    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

const TelegramConfig = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyEvent, setBusyEvent] = useState('');

  const loadDashboard = async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const data = await fetchWithAuth('/admin/notifications');
      setDashboard(data);
    } catch (err) {
      setError(err.message || 'Could not load notification settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem('userRole') !== 'ADMIN') {
      navigate('/', { replace: true });
      return;
    }
    loadDashboard();
  }, [navigate]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(''), 3500);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const updateEvent = async (event) => {
    setBusyEvent(event.type);
    setError('');
    try {
      const data = await fetchWithAuth(`/admin/notifications/${event.type}`, {
        method: 'PUT',
        body: { enabled: !event.enabled },
      });
      setDashboard(data);
      setNotice(`${event.displayName} ${!event.enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      setError(err.message || 'The setting could not be saved.');
    } finally {
      setBusyEvent('');
    }
  };

  const testEvent = async (event) => {
    setBusyEvent(`test-${event.type}`);
    setError('');
    try {
      const response = await fetchWithAuth(`/admin/notifications/${event.type}/test`, { method: 'POST' });
      setNotice(response.message || `${event.displayName} test queued.`);
      await loadDashboard(true);
    } catch (err) {
      setError(err.message || 'The test notification could not be sent.');
    } finally {
      setBusyEvent('');
    }
  };

  const updateAllEvents = async (enabled) => {
    const eventsToUpdate = (dashboard?.events || []).filter(event => event.enabled !== enabled);
    if (eventsToUpdate.length === 0) return;

    setBusyEvent(enabled ? 'all-on' : 'all-off');
    setError('');
    try {
      await Promise.all(eventsToUpdate.map(event => fetchWithAuth(`/admin/notifications/${event.type}`, {
        method: 'PUT',
        body: { enabled },
      })));
      await loadDashboard(true);
      setNotice(enabled ? 'All notifications enabled.' : 'All notifications disabled.');
    } catch (err) {
      await loadDashboard(true);
      setError(err.message || 'Some notification settings could not be saved.');
    } finally {
      setBusyEvent('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600"><ArrowPathIcon className="h-5 w-5 animate-spin" /> Loading notification control center…</div>
      </div>
    );
  }

  const simulationReady = dashboard?.testMode;
  const connectionReady = dashboard?.configured;
  const statusReady = connectionReady || simulationReady;
  const statusTitle = connectionReady ? 'Connected and active' : simulationReady ? 'Simulation mode active' : 'Configuration incomplete';
  const dailyPercent = Math.min(100, Math.round(((dashboard?.sentToday || 0) / Math.max(1, dashboard?.dailyAdvisoryThreshold || 1000)) * 100));
  const minutePercent = Math.min(100, Math.round(((dashboard?.sentLastMinute || 0) / Math.max(1, dashboard?.rollingMinuteGuidance || 20)) * 100));
  const progressTone = dashboard?.loadLevel === 'HIGH' ? 'bg-rose-500' : dashboard?.loadLevel === 'ELEVATED' ? 'bg-amber-500' : 'bg-emerald-500';
  const events = dashboard?.events || [];
  const globalBusy = busyEvent === 'all-on' || busyEvent === 'all-off';
  const allEnabled = events.length > 0 && events.every(event => event.enabled);
  const allDisabled = events.every(event => !event.enabled);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 text-slate-950">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900">
          <ChevronLeftIcon className="h-4 w-4" /> Back to admin
        </button>

        <header className="overflow-hidden rounded-3xl bg-slate-950 px-6 py-8 text-white shadow-xl sm:px-9 sm:py-10">
          <div className="flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300 ring-1 ring-white/10">
                <PaperAirplaneIcon className="h-4 w-4" /> Telegram control center
              </div>
              <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">Telegram & Notifications</h1>
              <p className="mt-4 max-w-xl text-sm leading-6 text-slate-300 sm:text-base">Choose exactly which platform events reach the configured Telegram destination, preview every template, and monitor delivery pressure.</p>
            </div>
            <div className={`inline-flex w-fit items-center gap-3 rounded-2xl px-4 py-3 ring-1 ${statusReady ? 'bg-emerald-400/10 text-emerald-300 ring-emerald-300/20' : 'bg-rose-400/10 text-rose-300 ring-rose-300/20'}`}>
              {statusReady ? <CheckCircleIcon className="h-6 w-6" /> : <XCircleIcon className="h-6 w-6" />}
              <div><p className="text-sm font-semibold">{statusTitle}</p><p className="text-xs opacity-70">{connectionReady ? dashboard?.destination : simulationReady ? 'Messages are recorded without contacting Telegram' : dashboard?.destination}</p></div>
            </div>
          </div>
        </header>

        {error && <div className="mt-5 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800"><ExclamationTriangleIcon className="h-5 w-5 flex-none" /><span>{error}</span></div>}
        {notice && <div className="mt-5 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"><CheckCircleIcon className="h-5 w-5 flex-none" /><span>{notice}</span></div>}

        <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric label="Messages today" value={dashboard?.sentToday ?? 0} note={dashboard?.testMode ? 'Delivered + simulated' : 'Confirmed by Telegram'} icon={PaperAirplaneIcon} tone="slate" />
          <Metric label="Last 60 seconds" value={dashboard?.sentLastMinute ?? 0} note="Rolling delivery volume" icon={BoltIcon} tone="cyan" />
          <Metric label="Failed today" value={dashboard?.failedToday ?? 0} note="API or network failures" icon={ExclamationTriangleIcon} tone={(dashboard?.failedToday ?? 0) > 0 ? 'rose' : 'white'} />
          <Metric label="Tests today" value={dashboard?.testsToday ?? 0} note="Included in message count" icon={BeakerIcon} tone="white" />
        </section>

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className={`rounded-xl p-2.5 ${dashboard?.loadLevel === 'HIGH' ? 'bg-rose-100 text-rose-700' : dashboard?.loadLevel === 'ELEVATED' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}><SignalIcon className="h-6 w-6" /></div>
              <div><div className="flex items-center gap-2"><h2 className="font-semibold">Delivery load</h2><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{dashboard?.loadLevel}</span></div><p className="mt-1 max-w-3xl text-sm text-slate-500">{dashboard?.loadMessage}</p></div>
            </div>
            <button onClick={() => loadDashboard()} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><ArrowPathIcon className="h-4 w-4" /> Refresh</button>
          </div>
          <div className="mt-5 grid gap-5 border-t border-slate-100 pt-5 md:grid-cols-2">
            <div>
              <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-600"><span>Rolling minute</span><span>{dashboard?.sentLastMinute || 0} / {dashboard?.rollingMinuteGuidance || 20}</span></div>
              <div className="h-3 overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-label="Rolling minute Telegram load" aria-valuemin="0" aria-valuemax={dashboard?.rollingMinuteGuidance || 20} aria-valuenow={dashboard?.sentLastMinute || 0}><div className={`h-full rounded-full transition-all duration-500 ${progressTone}`} style={{ width: `${minutePercent}%` }} /></div>
              <p className="mt-1.5 text-xs text-slate-400">{minutePercent}% of Telegram group guidance</p>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-600"><span>Daily advisory</span><span>{dashboard?.sentToday || 0} / {dashboard?.dailyAdvisoryThreshold || 1000}</span></div>
              <div className="h-3 overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-label="Daily notification advisory load" aria-valuemin="0" aria-valuemax={dashboard?.dailyAdvisoryThreshold || 1000} aria-valuenow={dashboard?.sentToday || 0}><div className={`h-full rounded-full transition-all duration-500 ${progressTone}`} style={{ width: `${dailyPercent}%` }} /></div>
              <p className="mt-1.5 text-xs text-slate-400">{dailyPercent}% of the internal advisory threshold</p>
            </div>
          </div>
        </section>

        <section className="mt-10">
          <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div><h2 className="text-2xl font-semibold tracking-tight">All notification toggles</h2><p className="mt-1 text-sm text-slate-500">Every channel starts paused. Tests cannot bypass a paused channel.</p></div>
            <div className="flex flex-col gap-3 sm:items-end">
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => updateAllEvents(true)} disabled={globalBusy || allEnabled || events.length === 0} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40">
                  {busyEvent === 'all-on' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckCircleIcon className="h-4 w-4" />} Check all
                </button>
                <button type="button" onClick={() => updateAllEvents(false)} disabled={globalBusy || allDisabled || events.length === 0} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
                  {busyEvent === 'all-off' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <XCircleIcon className="h-4 w-4" />} Uncheck all
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheckIcon className="h-4 w-4" /> Admin-only controls</div>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            {events.map(event => {
                    const saving = busyEvent === event.type;
                    const testing = busyEvent === `test-${event.type}`;
                    return (
                      <article key={event.type} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-slate-300 hover:shadow-md">
                        <div className="flex items-start justify-between gap-5">
                          <div><div className="flex flex-wrap items-center gap-2"><h3 className="font-semibold text-slate-900">{event.displayName}</h3><span className={`h-2 w-2 rounded-full ${event.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} /><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${CATEGORY_COLORS[event.category] || CATEGORY_COLORS.Engagement}`}>{event.category}</span></div><p className="mt-1 text-sm leading-5 text-slate-500">{event.description}</p></div>
                          <span className="flex-none rounded-lg bg-slate-50 px-2 py-1 text-xs font-medium text-slate-500"><ClockIcon className="mr-1 inline h-3.5 w-3.5" />{event.sentToday} today</span>
                        </div>
                        <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                          <div className="flex items-center gap-3"><Toggle checked={event.enabled} disabled={globalBusy || saving || testing} onChange={() => updateEvent(event)} label={`${event.enabled ? 'Disable' : 'Enable'} ${event.displayName}`} /><span className="text-sm font-medium text-slate-600">{saving ? 'Saving…' : event.enabled ? 'Active' : 'Paused'}</span></div>
                          <button disabled={globalBusy || (!dashboard?.configured && !dashboard?.testMode) || !event.enabled || saving || testing} title={!event.enabled ? 'Enable this notification before testing it' : 'Send test notification'} onClick={() => testEvent(event)} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40">
                            {testing ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <BeakerIcon className="h-4 w-4" />} {testing ? 'Sending…' : 'Send test'}
                          </button>
                        </div>
                      </article>
                    );
                  })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default TelegramConfig;
